import { useCallback, useState, type ReactNode } from 'react';
import { closesOnSave, fileTypeMissing, handsOffToScanConfirm, landedNames, NEEDS_CLASSIFICATION, OTHER_FILE_TYPE_CHOICE, queueBusy, resolveDocumentClass, Size, t, type FileTypeRow, type ModuleDefaultDocumentClass, type QueuedFile, type UploadFn } from '@bms2/shared';
import { AlertBanner } from '../controls/AlertBanner';
import { Select } from '../controls';
import { DropZone } from '../composites';
import { Modal } from './Modal';
import styles from './modal.module.css';

/**
 * The Upload File modal (piece 5, decision 0249). It composes the Modal shell
 * around the File drop zone of Composite and data-display components, so
 * multi-file upload, per-file progress and per-file retry come from there and
 * are not rebuilt: one unreadable photo never discards nine good ones.
 *
 * `fileTypeId` is required and has no default, because a default class of
 * General would make "forgot to pick a type" the widest setting in the system.
 * The class itself is resolved from three sources in order and shown
 * read-only, so the person sees what it became without being asked a question
 * they cannot answer.
 *
 * 🔒 A file starts the moment it is queued with a type, and the two buttons say
 * so (decision 2422): Done closes the window once nothing waits or travels, and
 * Stop aborts the request in flight, stops the files still queued and names
 * the ones that had already landed. Stop with nothing in flight closes the
 * window, and the × and Escape act as Stop does.
 */
export const uploadModalDocumentedStates = ['noType', 'resolved', 'needsClassification', 'scanConfirm'] as const;

export interface UploadFileModalProps {
  open: boolean;
  subtitle?: string;
  fileTypes: readonly FileTypeRow[];
  /**
   * 🔒 Decision 7850: the rest of the types that apply, where the act that opened the window cut the list to its own.
   * The list ends on Other Type, which reveals them; empty or absent, the list is `fileTypes` alone.
   */
  otherFileTypes?: readonly FileTypeRow[];
  /** The class the record the upload sits on declares, where it declares one. */
  recordDeclaredClass: string | null;
  /** The seeded row for that record's module, declared in The permission model. */
  moduleDefault: ModuleDefaultDocumentClass | null;
  upload: UploadFn;
  onCancel: () => void;
  /** The photo pipeline's confirm step; the modal stays open and hands over. */
  onScanConfirm?: (fileTypeId: string) => void;
  onSave: (outcome: { fileTypeId: string; documentClass: string }) => void;
  openerId?: string | null;
  autoStart?: boolean;
  /** A file type to open on, for the harness's named states. */
  initialFileTypeId?: string | null;
  /** Files dropped on the tile that opened the modal; they wait in the zone until a type is picked. */
  initialFiles?: readonly File[];
  /** A question the page's upload is waiting on, drawn under the queue: the same-name question (decision 2421), or Possible duplicate (decision 3014). */
  question?: ReactNode;
  /** 🔒 Decision 3014: the window widens while a question draws two files side by side. */
  wide?: boolean;
  /** 🔒 Decision 5440: a choice of the page's own drawn under the class line and above the drop zone, a Deal's photo Kind. */
  /** 🔒 T2928: a function is handed the file type the person has chosen, so a page's choice can follow it. */
  aboveZone?: ReactNode | ((state: { fileTypeId: string | null }) => ReactNode);
  /**
   * 🔒 Decision 11300: whether the chosen type holds the queue for the person's Upload, so what the window asks about a
   * file is answered before it is sent. Absent, or answering false, a file starts the moment it has a type (decision 2422).
   */
  holdQueue?: (fileTypeId: string | null) => boolean;
  /**
   * 🔒 Decision 11300: a sentence that holds Upload while something the window asks is refused, a date after today;
   * a function reads it off the queue and the type chosen, so each file's own fields can hold it (decision 11301).
   */
  sendProblem?: string | null | ((queue: readonly QueuedFile[], fileTypeId: string | null) => string | null);
  /** 🔒 Decision 11301: a queued file's own fields, its name and its date, handed the type chosen; drawn under the file's name. */
  rowFields?: (file: QueuedFile, fileTypeId: string | null) => ReactNode;
}

export function UploadFileModal({
  open,
  subtitle,
  fileTypes: offeredTypes,
  otherFileTypes = [],
  recordDeclaredClass,
  moduleDefault,
  upload,
  onCancel,
  onScanConfirm,
  onSave,
  openerId = null,
  autoStart = true,
  initialFileTypeId = null,
  initialFiles = [],
  question = null,
  wide = false,
  aboveZone = null,
  holdQueue,
  sendProblem = null,
  rowFields,
}: UploadFileModalProps) {
  const [fileTypeId, setFileTypeId] = useState<string | null>(initialFileTypeId);
  // 🔒 Decision 7850: Other Type reveals the rest once, and the list then stays whole while the window is open.
  const [revealed, setRevealed] = useState(false);
  // T3194: a window reopened shows the cut list again, with Other Type, whatever the last opening revealed.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setRevealed(false);
  }
  const cut = otherFileTypes.length > 0 && !revealed;
  const fileTypes = revealed ? [...offeredTypes, ...otherFileTypes] : offeredTypes;
  const [scanning, setScanning] = useState(false);
  const [queue, setQueue] = useState<readonly QueuedFile[]>([]);
  const [stopCount, setStopCount] = useState(0);
  // 🔒 Whether Stop was pressed; the names it reports are read from the queue at each draw, so the summary and the rows never disagree.
  const [stopped, setStopped] = useState(false);
  const fileType = fileTypes.find((f) => f.id === fileTypeId) ?? null;
  const resolved = resolveDocumentClass(fileType, recordDeclaredClass, moduleDefault);
  const missing = fileTypeMissing(fileTypeId);
  const busy = queueBusy(queue);
  const landed = landedNames(queue);
  // 🔒 Stop with nothing in flight closes the window, a file still waiting for its type included (piece 5; decision 2422).
  const inFlight = queue.some((file) => file.status === 'uploading');
  // 🔒 Decision 11300: a held queue waits for Upload, then runs to its end; a file added after it waits for the next press.
  const held = holdQueue ? holdQueue(fileTypeId) : false;
  const [sending, setSending] = useState(false);
  // 🔒 Row T3673: the files waiting at the press are the ones it sends; one added while they travel waits for the next press.
  const [released, setReleased] = useState<ReadonlySet<string>>(new Set());
  if (sending && !queue.some((file) => (file.status === 'queued' || file.status === 'uploading') && released.has(file.id))) setSending(false);
  const mayStart = useCallback((id: string) => !held || released.has(id), [held, released]);
  const waiting = held && !sending && queue.some((file) => file.status === 'queued');
  const sendRefusal = typeof sendProblem === 'function' ? sendProblem(queue, fileTypeId) : sendProblem;

  const save = () => {
    if (missing || !fileTypeId || busy) return;
    onSave({ fileTypeId, documentClass: resolved.documentClass });
    if (handsOffToScanConfirm(fileType)) {
      setScanning(true);
      onScanConfirm?.(fileTypeId);
      return;
    }
    if (closesOnSave(fileType)) onCancel();
  };

  /** Stop: with files waiting or travelling it stops them and stays open naming what landed; with none it closes. */
  const stop = () => {
    if (!inFlight) {
      onCancel();
      return;
    }
    setStopped(true);
    setStopCount((n) => n + 1);
  };
  const watchQueue = useCallback((next: readonly QueuedFile[]) => {
    // A file that lands after the abort was sent joins the names shown, because they are read from this queue.
    setQueue(next);
  }, []);

  const modalProps = {
    open,
    title: t('upload.title'),
    onCancel: stop,
    openerId,
    dataModal: 'upload',
    width: wide ? ('document' as const) : ('regular' as const),
    actions: [
      waiting
        ? { kind: 'primary' as const, label: t('upload.send'), onAct: () => { setReleased(new Set(queue.filter((file) => file.status === 'queued').map((file) => file.id))); setSending(true); }, disabled: missing || sendRefusal !== null, ...(sendRefusal === null ? {} : { disabledReason: sendRefusal }) }
        : { kind: 'primary' as const, label: t('upload.save'), onAct: save, disabled: missing || busy },
      { kind: 'cancel' as const, label: t('upload.stop'), onAct: stop },
    ],
    ...(subtitle === undefined ? {} : { subtitle }),
  };

  return (
    <Modal {...modalProps}>
      {/* No entry standing for "none": the picker refuses the save instead. */}
      <Select size={Size.Medium}
        label={t('upload.fileType')}
        required
        value={fileTypeId ?? ''}
        onChange={(next) => {
          if (next === OTHER_FILE_TYPE_CHOICE) setRevealed(true);
          else setFileTypeId(next === '' ? null : next);
        }}
        placeholder={t('upload.pickAType')}
        options={[...fileTypes.map((row) => ({ value: row.id, label: row.name })), ...(cut ? [{ value: OTHER_FILE_TYPE_CHOICE, label: t('upload.otherType') }] : [])]}
        dataAttribute={{ 'data-upload-field': 'fileType' }}
      />

      <p className={styles.readOnly} data-upload-class={resolved.documentClass} data-upload-class-source={resolved.source}>
        {t('upload.class')} {resolved.documentClass === NEEDS_CLASSIFICATION ? t('upload.needsClassification') : resolved.documentClass}
      </p>

      {typeof aboveZone === 'function' ? aboveZone({ fileTypeId }) : aboveZone}

      {/* 🔨 A file is never sent without its type: the queue waits until one is
          picked, and the chosen type rides with each file so the upload can
          name it (piece 5). 💰 The zone started on drop with no type yet chosen,
          so a real upload had nothing to tag the file with (found while
          building, 2026-09-09). */}
      <DropZone upload={(file, onProgress, signal) => upload({ ...file, fileTypeId }, onProgress, signal)} autoStart={autoStart && !missing && (!held || sending)} mayStart={mayStart} initialFiles={initialFiles} stopCount={stopCount} onQueueChange={watchQueue} {...(rowFields ? { rowFields: (file: QueuedFile) => rowFields(file, fileTypeId) } : {})} />

      {question}
      {waiting ? <p className={styles.readOnly} data-upload-held="true">{t('upload.heldHint')}</p> : busy ? <p className={styles.readOnly} data-upload-busy="true">{t('upload.busyHint')}</p> : null}
      {stopped ? <AlertBanner tone="info" message={landed.length === 0 ? t('upload.stoppedNone') : `${t('upload.stoppedLanded')} ${landed.join(', ')}`} /> : null}

      {scanning ? (
        <p className={styles.band} data-scan-confirm="true">
          {t('upload.scanConfirm')}
        </p>
      ) : null}
    </Modal>
  );
}
